import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Eye, ArrowRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    document.title = 'Blog | TesisKirala.com - Halı Saha & Spor Tesisi Rehberi';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', "TesisKirala.com blog: halı saha kiralama rehberleri, spor tesisi tavsiyeleri, şehir bazlı rezervasyon ipuçları ve daha fazlası.");
    }
    fetchPosts();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFiltered(posts);
    } else {
      setFiltered(posts.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        (p.keywords || []).some(k => k.toLowerCase().includes(q))
      ));
    }
  }, [posts, search]);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/blog/posts?limit=100`);
      setPosts(data.items || []);
    } catch (e) {
      console.error('blog list error', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 py-16 px-4">
        <div className="max-w-5xl mx-auto text-center text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="blog-title">
            TesisKirala Blog
          </h1>
          <p className="text-base sm:text-lg text-emerald-50 max-w-2xl mx-auto">
            Halı saha kiralama, spor tesisi seçimi ve rezervasyon dünyasından
            rehberler, ipuçları ve haberler.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Search */}
        <div className="relative max-w-xl mx-auto mb-10">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Yazı ara..."
            className="pl-10"
            data-testid="blog-search-input"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-slate-500 py-20" data-testid="blog-empty-state">
            {posts.length === 0 ? 'Henüz blog yazısı yok.' : 'Aramanızla eşleşen yazı bulunamadı.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {filtered.map((post) => (
              <Link
                key={post.post_id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow border border-slate-200"
                data-testid={`blog-card-${post.slug}`}
              >
                <div className="h-48 bg-gradient-to-br from-emerald-100 to-emerald-200 overflow-hidden">
                  {post.cover_image ? (
                    <img
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-emerald-700 text-6xl font-bold opacity-30">
                      TK
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(post.published_at)}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {post.view_count || 0}</span>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-emerald-700 line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-sm text-slate-600 line-clamp-3 mb-4">{post.excerpt}</p>
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600 group-hover:gap-2 transition-all">
                    Devamını Oku <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
